import { useState } from "react";
import { motion } from "framer-motion";
import { Download, BookOpen } from "lucide-react";
import { useLang } from "../lang";
import Breadcrumb from "../components/Breadcrumb";
import BrandEyebrow from "../components/BrandEyebrow";
import CatalogViewer from "../components/CatalogViewer";
import { CATALOGS } from "../catalogs";

function CatalogCard({ c, lang, onOpen, i }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.1 * i }}
      className={`grid gap-8 md:gap-14 items-center ${i % 2 ? "md:grid-cols-[1fr_1.4fr] md:[&>*:first-child]:order-2" : "md:grid-cols-[1.4fr_1fr]"}`}
      data-testid={`catalog-card-${c.id}`}
    >
      <button
        onClick={onOpen}
        className="group relative rounded-3xl overflow-hidden bg-[#f5f5f7] p-4 md:p-8"
        data-testid={`catalog-cover-${c.id}`}
      >
        <img src={c.pages[0]} alt={c.title[lang]} className="w-full h-auto rounded-xl shadow-[0_30px_60px_-20px_rgba(0,0,0,0.35)] transition-transform duration-700 group-hover:scale-[1.02]" />
      </button>
      <div>
        <div className="text-[12px] uppercase tracking-[0.18em] text-[#1D3F94] font-medium">{c.tag[lang]}</div>
        <h2 className="mt-3 text-[28px] md:text-[40px] font-semibold tracking-[-0.02em] text-[#1d1d1f] leading-[1.08]">{c.title[lang]}</h2>
        <p className="mt-4 text-[16px] md:text-[17px] text-[#1d1d1f]/70 leading-[1.6]">{c.desc[lang]}</p>
        <div className="mt-3 text-[13px] text-[#1d1d1f]/50">{c.pages.length} {lang === "it" ? "pagine" : "pages"} · PDF · {c.size}</div>
        <div className="mt-8 flex flex-wrap gap-3">
          <button onClick={onOpen} data-testid={`catalog-open-${c.id}`} className="inline-flex items-center gap-2 rounded-full bg-[#1D3F94] text-white px-6 py-3 text-[14px] font-medium hover:bg-[#163275] transition-colors">
            <BookOpen className="w-4 h-4" /> {lang === "it" ? "Sfoglia online" : "Browse online"}
          </button>
          <a href={c.pdf} download data-testid={`catalog-download-${c.id}`} className="inline-flex items-center gap-2 rounded-full border border-[#1d1d1f]/15 text-[#1d1d1f] px-6 py-3 text-[14px] font-medium hover:border-[#1D3F94] hover:text-[#1D3F94] transition-colors">
            <Download className="w-4 h-4" /> {lang === "it" ? "Scarica PDF" : "Download PDF"}
          </a>
        </div>
      </div>
    </motion.article>
  );
}

export default function Cataloghi() {
  const { lang } = useLang();
  const [open, setOpen] = useState(null);
  return (
    <main className="min-h-screen bg-white pt-20" data-testid="cataloghi-page">
      <div className="max-w-[1200px] mx-auto px-6 pt-8">
        <Breadcrumb items={[{ label: "Home", to: "/" }, { label: lang === "it" ? "Cataloghi" : "Catalogues" }]} />
      </div>
      <section className="pt-8 pb-12 max-w-[900px] mx-auto px-6 text-center">
        <div className="mb-5"><BrandEyebrow>{lang === "it" ? "Cataloghi" : "Catalogues"}</BrandEyebrow></div>
        <h1 className="text-[36px] md:text-[56px] font-semibold tracking-[-0.02em] text-[#1d1d1f] leading-[1.05]">
          {lang === "it" ? "Sfoglia i nostri cataloghi" : "Browse our catalogues"}
        </h1>
        <p className="mt-6 text-[17px] md:text-[19px] text-[#1d1d1f]/70 leading-[1.6]">
          {lang === "it" ? "Tutti i nostri sistemi raccolti in cataloghi da consultare online o scaricare in PDF." : "All our systems collected in catalogues to browse online or download as PDF."}
        </p>
      </section>
      <section className="max-w-[1200px] mx-auto px-6 pb-24 space-y-20 md:space-y-28">
        {CATALOGS.map((c, i) => <CatalogCard key={c.id} c={c} i={i} lang={lang} onOpen={() => setOpen(c)} />)}
        <p className="text-center text-[14px] text-[#1d1d1f]/50" data-testid="catalogs-coming-soon">
          {lang === "it" ? "Nuovi cataloghi in arrivo." : "More catalogues coming soon."}
        </p>
      </section>
      {open && <CatalogViewer catalog={open} lang={lang} onClose={() => setOpen(null)} />}
    </main>
  );
}
